import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import demoVideo from './section1.mp4';
import './MainDemo.css';

const MainDemo = () => {
  // Chat & Animation States
  const [chatProgress, setChatProgress] = useState(0);
  const [typingIndex, setTypingIndex] = useState(0);
  const [isSendClicked, setIsSendClicked] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);

  // Refs
  const videoContainerRef = useRef(null);
  const videoRef = useRef(null);
  const frameRef = useRef(null);
  const overlayRef = useRef(null);
  const chatRef = useRef(null);
  const [fixedState, setFixedState] = useState('absolute-top');
  const [overlayOpacity, setOverlayOpacity] = useState(1);

  // Auto-scroll Logic Refs
  const autoScrollFrameRef = useRef(null);
  const userInteractionTimeoutRef = useRef(null);
  const isUserInteractingRef = useRef(false);

  // --- Scrollytelling & Animation Logic ---
  useEffect(() => {
    const handleScrollVideo = () => {
      if (!videoContainerRef.current || !videoRef.current) return;

      const container = videoContainerRef.current;
      const video = videoRef.current;
      const rect = container.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const containerHeight = container.offsetHeight;

      // 1. Positioning State
      if (rect.top <= 0 && rect.bottom >= viewportHeight) {
        setFixedState('fixed');
      } else if (rect.bottom < viewportHeight) {
        setFixedState('absolute-bottom');
      } else {
        setFixedState('absolute-top');
      }

      // 2. Progress Calculation (0 to 1)
      const scrollableDistance = containerHeight - viewportHeight;
      const scrolled = Math.max(0, Math.min(-rect.top, scrollableDistance));
      const progress = scrolled / scrollableDistance;

      setChatProgress(progress);

      // 3. Animation Sequence Logic
      const TYPE_FINISH = 0.36;
      const ACTION_POINT = 0.43;
      const RESET_POINT = 0.28;

      // A. TRIGGER ACTION (Down Scroll)
      if (progress > ACTION_POINT && !isSendClicked) {
        setIsSendClicked(true);
        setTimeout(() => {
            setVideoPlaying(true);
            setOverlayOpacity(0);
            if(video.paused) video.play();
        }, 300);
      }
      // B. FORCE RESET (Up Scroll)
      else if (progress < RESET_POINT && isSendClicked) {
        setIsSendClicked(false);
        setVideoPlaying(false);
        setOverlayOpacity(1);
        if(!video.paused) {
            video.pause();
            video.currentTime = 0;
        }
      }
    };

    window.addEventListener('scroll', handleScrollVideo);
    handleScrollVideo();
    return () => window.removeEventListener('scroll', handleScrollVideo);
  }, [isSendClicked]);

  // --- Typing Effect ---
  const chatMessage = "Can you style the base glass curtain wall, podium simple straight balcony and tower parametric grid?";

  const getOverlayTextOpacity = () => {
    const fadeOutStart = 0.26;
    const fadeOutEnd = 0.43;

    if (isSendClicked) return 0;
    if (chatProgress <= fadeOutStart) return 1;
    if (chatProgress < fadeOutEnd) {
      return 1 - ((chatProgress - fadeOutStart) / (fadeOutEnd - fadeOutStart));
    }
    return 0;
  };

  const handleVideoReady = () => {
    setIsVideoLoading(false);
  };

  useEffect(() => {
    const typeEndProgress = 0.36;

    if (chatProgress <= typeEndProgress) {
        const percentTyped = chatProgress / typeEndProgress;
        const charIndex = Math.floor(percentTyped * chatMessage.length);
        setTypingIndex(charIndex);
    } else {
        setTypingIndex(chatMessage.length);
    }
  }, [chatProgress]);

  useLayoutEffect(() => {
    let raf = 0;

    const updateOverlayLayout = () => {
      raf = 0;

      const frame = frameRef.current;
      const overlay = overlayRef.current;
      const chat = chatRef.current;
      if (!frame || !overlay || !chat) return;

      const fr = frame.getBoundingClientRect();
      const cr = chat.getBoundingClientRect();
      const padding = 18;
      const gap = 18;

      // Keep the overlay inside the available space above the chat box.
      const safeMaxHeight = Math.max(80, (cr.top - fr.top) - gap - padding);
      overlay.style.top = `${padding}px`;
      overlay.style.maxHeight = `${safeMaxHeight}px`;
      overlay.style.overflow = 'hidden';
      overlay.classList.toggle('tight', safeMaxHeight < 220);
    };

    const scheduleUpdate = () => {
      if (raf) return;
      raf = requestAnimationFrame(updateOverlayLayout);
    };

    requestAnimationFrame(() => requestAnimationFrame(updateOverlayLayout));

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    let ro;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(scheduleUpdate);
      if (frameRef.current) ro.observe(frameRef.current);
      if (chatRef.current) ro.observe(chatRef.current);
      if (overlayRef.current) ro.observe(overlayRef.current);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleUpdate).catch(() => {});
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (ro) ro.disconnect();
    };
  }, []);

  // --- Auto-Scroll Engine ---
  useEffect(() => {
    const isAppleDesktop = /Mac/.test(navigator.platform) && navigator.maxTouchPoints === 0;
    if (isAppleDesktop) return undefined;

    const handleAutoScrollEngine = () => {
      if (!videoContainerRef.current) return;

      const autoScrollDisabledUntil = window.__disableSectionAutoScrollUntil || 0;
      if (Date.now() < autoScrollDisabledUntil) {
        autoScrollFrameRef.current = requestAnimationFrame(handleAutoScrollEngine);
        return;
      }

      const container = videoContainerRef.current;
      const rect = container.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const isInsideSection = rect.top <= 0 && rect.bottom > viewportHeight;

      if (isInsideSection && !isUserInteractingRef.current) {
        const totalScrollableDistance = container.offsetHeight - viewportHeight;
        const totalFrames = 10 * 60; // 10 seconds for 250vh
        const pixelsPerFrame = totalScrollableDistance / totalFrames;
        window.scrollBy(0, pixelsPerFrame);
      }
      autoScrollFrameRef.current = requestAnimationFrame(handleAutoScrollEngine);
    };
    autoScrollFrameRef.current = requestAnimationFrame(handleAutoScrollEngine);

    const handleUserInteraction = () => {
      isUserInteractingRef.current = true;
      if (userInteractionTimeoutRef.current) clearTimeout(userInteractionTimeoutRef.current);
      userInteractionTimeoutRef.current = setTimeout(() => {
        isUserInteractingRef.current = false;
      }, 1500);
    };

    window.addEventListener('wheel', handleUserInteraction);
    window.addEventListener('touchmove', handleUserInteraction);
    window.addEventListener('keydown', handleUserInteraction);

    return () => {
      if (autoScrollFrameRef.current) cancelAnimationFrame(autoScrollFrameRef.current);
      window.removeEventListener('wheel', handleUserInteraction);
      window.removeEventListener('touchmove', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  return (
    <section className="scrolly-video-section" ref={videoContainerRef}>
      <div className={`sticky-wrapper ${fixedState}`}>

        {/* Video Background */}
        <div className="video-background">
          <div className="video-frame-stack">
            <div className="video-frame" ref={frameRef}>
              <video
                ref={videoRef}
                className="scrub-video"
                playsInline
                webkit-playsinline="true"
                muted
                loop={false}
                preload="auto"
                onLoadedData={handleVideoReady}
                onCanPlay={handleVideoReady}
                onError={handleVideoReady}
                src={demoVideo}
              />
              {isVideoLoading && (
                <div className="video-loader-overlay" aria-live="polite">
                  <div className="video-loader-spinner" />
                  <span className="video-loader-text">Loading demo...</span>
                </div>
              )}
              <div className="video-dimmer" style={{ opacity: overlayOpacity }}></div>
              <div
                ref={overlayRef}
                className="main-demo-text-overlay"
                style={{ opacity: getOverlayTextOpacity() }}
              >
                <h2 className="main-demo-title"></h2>
              </div>
            </div>
            {videoPlaying && (
              <div className="actual-screen-record-tag">
                {'< This is an actual screen record >'}
              </div>
            )}
          </div>
        </div>

        {/* Agent Mode Chat Box */}
        <div
          ref={chatRef}
          className={`agent-chat-container ${isSendClicked ? 'fade-out' : ''}`}
          style={{
              transform: `translate(-50%, -50%) scale(${isSendClicked ? 0.95 : 1})`,
              opacity: isSendClicked ? 0 : 1
          }}
        >
          {/* Header */}
          <div className="agent-header">
              <div className="agent-toggle">
                  <div className="agent-icon-box">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="16" r="2"/><path d="M12 6V3"/><path d="M8 6V4"/><path d="M16 6V4"/></svg>
                  </div>
              </div>
              <span className="agent-title">Agent Mode</span>
          </div>

          {/* Chat Area */}
          <div className="agent-body">
              <p className="agent-text">
                  {chatMessage.substring(0, typingIndex)}
                  {typingIndex < chatMessage.length && <span className="cursor"></span>}
              </p>
          </div>

          {/* Footer */}
          <div className="agent-footer">
              <div className="agent-controls-left">
                  <div className="agent-pill deepseek">
                      <svg className="icon-brain" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/></svg>
                      <span>INSTANTURBAN A1</span>
                  </div>
              </div>

              <div className="agent-controls-right">
                  <div className="agent-pill icon-only">YOLO</div>
                  <div className="agent-pill icon-only">EN</div>
                  <div className="agent-icon-btn">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                  </div>
                  <div className="agent-icon-btn">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  </div>

                  <div className={`agent-send-btn ${isSendClicked ? 'clicked' : ''}`}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2L12 22M12 2L2 12M12 2L22 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
              </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default MainDemo;
